import { DataTable } from '@/components'
import { useActivityTypeLabels } from '@/modules/activities/use-activity-type-labels'
import {
  formatSecondsAsHourMinute,
  getRejectedActivitiesTotal,
  getRejectedActivityKey,
  getWorkPeriodRejectedActivities,
  type RejectedActivityWorkPeriod,
} from '@/modules/salary/utils/get-rejected-activities'
import { format } from '@/utils'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'

interface UnpaidActivityRow {
  id: string
  activity: string
  time: string
  duration: string
  comment: string
  isSum: boolean
}

interface UnpaidActivitiesTableProps {
  workPeriod: RejectedActivityWorkPeriod
}

/**
 * The activities a manager has marked as not paid, with the comment explaining
 * why. Renders nothing when every activity in the work period is paid.
 */
export const UnpaidActivitiesTable: React.FC<UnpaidActivitiesTableProps> = ({
  workPeriod,
}) => {
  const translations = useTranslate({
    unpaidActivities: 'salary.unpaid-activities',
    activity: 'common.activity',
    time: 'common.time',
    duration: 'common.duration',
    comment: 'common.comment',
    estimated: 'salary.estimated',
    sum: 'common.sum',
  })
  const activityTypeLabels = useActivityTypeLabels()

  const rows = useMemo<UnpaidActivityRow[]>(() => {
    const rejectedActivities = getWorkPeriodRejectedActivities(workPeriod)
    if (rejectedActivities.length === 0) return []

    const activityRows = rejectedActivities.map(rejected => ({
      id: getRejectedActivityKey(rejected),
      activity: `${
        activityTypeLabels[rejected.activityType] ?? rejected.activityType
      }${rejected.isEstimate ? ` (${translations.estimated})` : ''}`,
      time: rejected.spans
        .map(
          span =>
            `${format(span.start, 'HH:mm')} - ${format(span.end, 'HH:mm')}`
        )
        .join(', '),
      duration: formatSecondsAsHourMinute(rejected.seconds),
      comment: rejected.comment,
      isSum: false,
    }))

    return [
      ...activityRows,
      {
        id: 'sum',
        activity: translations.sum,
        time: '',
        duration: formatSecondsAsHourMinute(
          getRejectedActivitiesTotal(rejectedActivities)
        ),
        comment: '',
        isSum: true,
      },
    ]
  }, [workPeriod, activityTypeLabels, translations.estimated, translations.sum])

  const column = createColumnHelper<UnpaidActivityRow>()
  const columns = useMemo(
    () => [
      column.accessor('activity', {
        header: translations.activity,
      }),
      column.accessor('time', {
        header: translations.time,
        meta: { narrow: true },
        cell: info => (
          <span className="whitespace-nowrap">{info.getValue()}</span>
        ),
      }),
      column.accessor('duration', {
        header: translations.duration,
        meta: { narrow: true, end: true },
      }),
      column.accessor('comment', {
        header: translations.comment,
        cell: info =>
          !info.row.original.isSum && (
            <span className="break-words whitespace-pre-wrap">
              {info.getValue() || '–'}
            </span>
          ),
      }),
    ],
    [
      column,
      translations.activity,
      translations.comment,
      translations.duration,
      translations.time,
    ]
  )

  if (rows.length === 0) return null

  return (
    <div className="mt-4">
      <h4 className="mb-1 font-semibold text-neutral-700">
        {translations.unpaidActivities}
      </h4>
      <DataTable
        columns={columns}
        data={rows}
        rowClassName={row => (row.isSum ? 'bg-gray-50 font-medium' : undefined)}
        inCard
      />
    </div>
  )
}
